import { useState } from "react"
import { useNavigate } from "react-router-dom"

import { useBalance } from "@/entities/user"

import { DepositSheet } from "@/features/wallet"

import IconButton from "@/shared/ui/IconButton"

import { useTournamentStore } from "@/shared/store"
import { TournamentBanner } from "@/features/tournament"

import { useI18n } from "@/shared/i18n"
import { useFeatureFlag } from "@/shared/features"
import { AnalyticsEvent, trackAnalyticsEvent } from "@/shared/lib/analytics"

import HistoryIcon from "@/assets/icons/history-icon.svg?react"
import TonIcon from "@/assets/icons/ton-icon.svg?react"
import PlusIcon from "@/assets/icons/plus-icon--green.svg?react"
import rhombusShapeUrl from "@/assets/textures/rhombus-shape-baked.svg?url"

/**
 * Balance pill background via horizontal 3-slice `border-image`. Native shape
 * is 73×44 with the hexagon tip on each end; we fix the end caps at 12px (the
 * source cap region, 1:1 with the 44px-tall button) and stretch only the
 * straight middle — the SVG no longer distorts at any width.
 */
const BALANCE_BG_STYLE = {
  borderStyle: "solid",
  borderColor: "transparent",
  borderWidth: "0 12px",
  // Quoted url(): inlined data-URI carries literal `'` for SVG attributes,
  // which a strict CSS parser may reject inside unquoted url(...).
  borderImageSource: `url("${rhombusShapeUrl}")`,
  borderImageSlice: "0 12 fill",
  borderImageWidth: "0 12px",
  borderImageRepeat: "stretch",
} as const

export function InfoBlock() {
  const { t } = useI18n()

  const navigate = useNavigate()

  const { data: balance } = useBalance()
  const balanceAmount = parseFloat(balance?.balance?.amount || "0")
  const displayBalance = balanceAmount.toFixed(2)

  const [isDepositOpen, setIsDepositOpen] = useState(false)

  const tournament = useTournamentStore((s) => s.summary)
  const tournamentsEnabled = useFeatureFlag("tournaments")

  return (
    <>
      <div className="sticky top-0 z-50 flex items-center gap-0 px-3 py-1">
        <IconButton
          aria-label={t("pvpRoom.ariaGamesHistory")}
          onClick={() => {
            navigate("/pvp-roulette-history")
          }}
          icon={<HistoryIcon className="size-7" />}
        />

        {/* CENTER: tournament banner when any tournament info exists, else reserved flex space */}
        {tournamentsEnabled && tournament ? (
          <TournamentBanner
            tournament={tournament}
            onClick={() => {
              trackAnalyticsEvent(AnalyticsEvent.TournamentLeaderboardOpen, {
                source: "pvp_banner",
              })
              navigate("/tournament-leaderboard")
            }}
          />
        ) : (
          <div className="h-14 min-w-0 flex-1 shrink" aria-hidden />
        )}

        <button
          aria-label={t("pvpRoom.deposit")}
          className="relative flex h-11 items-center justify-center gap-0.5"
          style={BALANCE_BG_STYLE}
          onClick={() => setIsDepositOpen(true)}
        >
          <div className="flex items-center gap-[3px]">
            <span className="text-sm/none font-semibold tracking-[-0.5px]">
              {displayBalance}
            </span>
            <TonIcon className="size-3.5" />
          </div>
          <PlusIcon className="size-5" />
        </button>
      </div>

      <DepositSheet
        isOpen={isDepositOpen}
        setIsOpen={setIsDepositOpen}
        depositUiSource="pvp_top_bar"
      />
    </>
  )
}
